import React from 'react';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { union, without } from 'lodash-es';
import { Checkbox } from '@ygtechnologies/rubus-ui';

interface CommonTreeProps<T> {
    idField: FieldSelector<T>;
    nameField: FieldSelector<T>;
    childrenField?: FieldSelector<T>;
    maxDepth?: number;
}

type ITreeFilterProps<T> = CommonTreeProps<T> & {
    data: T[];
    value: any[];
    onChange: (value: any[]) => void;
};

type ITreeFilterNodeProps<T> = CommonTreeProps<T> & {
    item: T;
    level: number;
    value: any[];
    onChange: (v: any, checked: boolean) => void;
};

type ITreeFilterListProps<T> = CommonTreeProps<T> & {
    items: T[];
    level: number;
    value: any[];
    onChange: (v: any, checked: boolean) => void;
};

interface SelectorContext {
    level: number;
}
type FieldSelector<T> = keyof T | ((item: T, context?: SelectorContext) => any);
function getFieldValue<T>(selector: FieldSelector<T>, object: T, context?: SelectorContext) {
    if (!selector) {
        return null;
    }

    if (typeof selector === 'function') {
        return selector(object, context);
    }

    return object[selector];
}

function TreeFilterNode<T extends object>({
    level,
    item,
    maxDepth,
    idField,
    nameField,
    childrenField,
    onChange,
    value,
}: ITreeFilterNodeProps<T>) {
    const getField = (selector: FieldSelector<T>) => {
        const selectorContext = { level };
        return getFieldValue(selector, item, selectorContext);
    };

    const getChildren = () => {
        if (!childrenField) {
            return [];
        }

        return getField(childrenField);
    };

    const handleChange = (e) => {
        triggerHandler(onChange, item, e.target.checked);
    };

    const maxDepthReached = !!maxDepth && level + 1 >= maxDepth;
    const id = getField(idField);
    const name = getField(nameField);
    const isChecked = value.includes(id);
    const children = getChildren();

    return (
        <React.Fragment>
            <Checkbox inputSize="md" checked={isChecked} value={id} onChange={handleChange}>
                {name}
            </Checkbox>
            {isChecked && !maxDepthReached && (
                <TreeFilterList
                    items={children}
                    level={level + 1}
                    value={value}
                    onChange={onChange}
                    idField={idField}
                    nameField={nameField}
                    childrenField={childrenField}
                    maxDepth={maxDepth}
                />
            )}
        </React.Fragment>
    );
}

function TreeFilterList<T extends object>({ items, ...rest }: ITreeFilterListProps<T>) {
    if (!items || !items.length) {
        return null;
    }

    return (
        <ul className="checkbox-list">
            {items.map((item) => (
                <li key={getFieldValue(rest.idField, item, { level: rest.level })}>
                    <TreeFilterNode {...rest} item={item} />
                </li>
            ))}
        </ul>
    );
}

function TreeFilter<T extends object>({
    data,
    value,
    maxDepth,
    onChange,
    idField,
    nameField,
    childrenField,
}: ITreeFilterProps<T>) {
    const getDescendants = (item: T, current: any[] = [], level = 0) => {
        if (!childrenField) {
            return [];
        }

        const children = getFieldValue(childrenField, item) || [];

        if (!Array.isArray(children) || children.length === 0) {
            return current;
        }

        children.forEach((sub) => {
            current.push(sub);
            getDescendants(sub, current, level + 1);
        });

        return current;
    };

    const handleCheckboxChange = (item: T, checked: boolean) => {
        const currentSelection = value;

        let newSelection: any[];

        if (checked) {
            newSelection = union(currentSelection, [getFieldValue(idField, item)]);
        } else {
            const uncheckIds = [getFieldValue(idField, item), ...getDescendants(item).map((x) => x.id)];

            newSelection = without(currentSelection, ...uncheckIds);
        }

        triggerHandler(onChange, newSelection);
    };

    const listProps = {
        level: 0,
        items: data,
        idField,
        nameField,
        childrenField,
        value,
        onChange: handleCheckboxChange,
        maxDepth,
    };

    return <TreeFilterList {...listProps} />;
}

export default TreeFilter;
